import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Sidebar = ({ isCollapsed = false, onToggleCollapse }) => {
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [sectionMetrics, setSectionMetrics] = useState({
    systemOverview: { servers: 24, health: 'good' },
    serverDetail: { selected: 'srv-prod-01', status: 'online' },
    campaignManager: { active: 12, pending: 3 }
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const getCurrentSection = () => {
    if (location?.pathname?.includes('/system-overview')) return 'system-overview';
    if (location?.pathname?.includes('/server-detail')) return 'server-detail';
    if (location?.pathname?.includes('/campaign-manager')) return 'campaign-manager';
    return null;
  };

  const getHealthIcon = (health) => {
    switch (health) {
      case 'good':
        return { name: 'CheckCircle2', color: 'text-success' };
      case 'warning':
        return { name: 'AlertTriangle', color: 'text-warning' };
      case 'critical':
        return { name: 'XCircle', color: 'text-error' };
      default:
        return { name: 'Circle', color: 'text-muted-foreground' };
    }
  };

  const currentSection = getCurrentSection();
  const healthIcon = getHealthIcon(sectionMetrics?.systemOverview?.health);

  return (
    <>
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="mobile-menu-button"
        aria-label="Toggle mobile menu"
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={24} />
      </button>
      {isMobileOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
      <aside
        className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <Icon name="Zap" size={28} className="sidebar-logo-icon" />
          </div>
          {!isCollapsed && (
            <span className="ml-3 text-lg font-heading font-semibold text-foreground">
              Chimera
            </span>
          )}
        </div>

        <div className="sidebar-content">
          <nav className="space-y-1">
            <Link
              to="/system-overview"
              className={`sidebar-nav-item ${
                isActivePath('/system-overview') ? 'active' : ''
              }`}
            >
              <Icon name="LayoutDashboard" size={20} />
              <span className="sidebar-nav-text text-sm font-medium">System Overview</span>
            </Link>

            <Link
              to="/campaign-manager"
              className={`sidebar-nav-item ${
                isActivePath('/campaign-manager') ? 'active' : ''
              }`}
            >
              <Icon name="Send" size={20} />
              <span className="sidebar-nav-text text-sm font-medium">Campaign Manager</span>
            </Link>

            <Link
              to="/war-room-dashboard"
              className={`sidebar-nav-item ${
                isActivePath('/war-room-dashboard') ? 'active' : ''
              }`}
            >
              <Icon name="Shield" size={20} />
              <span className="sidebar-nav-text text-sm font-medium">War Room</span>
            </Link>

            <Link
              to="/risk-aware-campaign-creator"
              className={`sidebar-nav-item ${
                isActivePath('/risk-aware-campaign-creator') ? 'active' : ''
              }`}
            >
              <Icon name="Target" size={20} />
              <span className="sidebar-nav-text text-sm font-medium">Risk-Aware Creator</span>
            </Link>

            <Link
              to="/data-intelligence-hub"
              className={`sidebar-nav-item ${
                isActivePath('/data-intelligence-hub') ? 'active' : ''
              }`}
            >
              <Icon name="BarChart3" size={20} />
              <span className="sidebar-nav-text text-sm font-medium">Data Intelligence</span>
            </Link>

            <Link
              to="/contact-lists-management"
              className={`sidebar-nav-item ${
                isActivePath('/contact-lists-management') ? 'active' : ''
              }`}
            >
              <Icon name="Users" size={20} />
              <span className="sidebar-nav-text text-sm font-medium">Contact Lists</span>
            </Link>
          </nav>

          {!isCollapsed && currentSection && (
            <div className="mt-8 p-4 bg-muted rounded-lg">
              <div className="text-xs font-caption font-medium text-muted-foreground uppercase tracking-wide mb-3">
                Section Metrics
              </div>

              {currentSection === 'system-overview' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-foreground">Active Servers</span>
                    <span className="text-sm font-mono font-medium text-foreground">
                      {sectionMetrics?.systemOverview?.servers}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-foreground">Fleet Health</span>
                    <Icon 
                      name={healthIcon?.name} 
                      size={16} 
                      className={healthIcon?.color} 
                    />
                  </div>
                </div>
              )}

              {currentSection === 'server-detail' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-foreground">Selected</span>
                    <span className="text-xs font-mono text-muted-foreground">
                      {sectionMetrics?.serverDetail?.selected}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-foreground">Status</span>
                    <span className="text-xs px-2 py-1 bg-success/10 text-success rounded font-medium">
                      {sectionMetrics?.serverDetail?.status}
                    </span>
                  </div>
                </div>
              )}

              {currentSection === 'campaign-manager' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-foreground">Active</span>
                    <span className="text-sm font-mono font-medium text-foreground">
                      {sectionMetrics?.campaignManager?.active}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-foreground">Pending</span>
                    <span className="text-sm font-mono font-medium text-warning">
                      {sectionMetrics?.campaignManager?.pending}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {!isCollapsed && (
            <div className="mt-8">
              <div className="text-xs font-caption font-medium text-muted-foreground uppercase tracking-wide mb-3">
                Quick Actions
              </div>
              <div className="space-y-2">
                <Link to="/server-detail">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Plus"
                    iconPosition="left"
                    fullWidth
                    className="justify-start"
                  >
                    Add Server
                  </Button>
                </Link>
                <Link to="/system-overview">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="FileText"
                    iconPosition="left"
                    fullWidth
                    className="justify-start"
                  >
                    View Logs
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            iconName={isCollapsed ? 'ChevronRight' : 'ChevronLeft'}
            iconPosition={isCollapsed ? 'left' : 'right'}
            fullWidth
            onClick={onToggleCollapse}
            className="justify-center lg:justify-start"
          >
            {!isCollapsed && 'Collapse'}
          </Button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;